import { useState } from 'react';
import type { ScoreGame } from '@shared/types/game';
import { runningTotals, seatTotals } from '@shared/game-logic';
import { Button, Panel } from '@/components/ui';
import { plural, seatColor, signed, trumpLabel } from '@/lib/labels';
import { useScorekeeper } from '@/store/scorekeeper';

/**
 * The scorecard as it would be on paper: rounds down, players across.
 *
 * Tapping a cell explains how the number was reached, which is the fastest way
 * to settle a "why did I get that?" at the table — and doubles as teaching.
 */
export default function Scorecard({ game }: { game: ScoreGame }) {
  const reopenRound = useScorekeeper((state) => state.reopenRound);
  const [selected, setSelected] = useState<{ roundNo: number; seat: number } | null>(null);

  const totals = runningTotals(game.rounds, game.seats.length);
  const finalTotals = seatTotals(game.rounds, game.seats.length);
  const { scoring } = game.config;

  const selectedRound = selected ? game.rounds.find((r) => r.roundNo === selected.roundNo) : null;
  const selectedEntry =
    selectedRound && selected
      ? selectedRound.entries.find((entry) => entry.seat === selected.seat)
      : null;

  return (
    <div className="space-y-4">
      <Panel title="Scorekaart">
        {/* A 7-player card is wider than a phone; let it scroll on its own. */}
        <div className="-mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-base-800 px-2 py-2 text-left text-xs font-medium text-slate-400">
                  Ronde
                </th>
                {game.seats.map((seat) => (
                  <th key={seat.seat} className="px-2 py-2 text-right font-medium">
                    <span className="flex items-center justify-end gap-1.5">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: seatColor(seat.seat) }}
                      />
                      <span className="max-w-20 truncate">{seat.displayName}</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {game.rounds.map((round, roundIndex) => (
                <tr key={round.roundNo} className="border-t border-white/5">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-base-800 px-2 py-2 text-left font-normal"
                  >
                    <span className="block tabular-nums">{round.roundNo}</span>
                    <span className="block text-xs text-slate-500">{round.cards}k</span>
                  </th>
                  {game.seats.map((seat) => {
                    const entry = round.entries.find((candidate) => candidate.seat === seat.seat);
                    const isSelected =
                      selected?.roundNo === round.roundNo && selected?.seat === seat.seat;

                    return (
                      <td key={seat.seat} className="px-1 py-1 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setSelected(
                              isSelected ? null : { roundNo: round.roundNo, seat: seat.seat }
                            )
                          }
                          className={[
                            'w-full rounded-lg px-2 py-1.5 text-right transition-colors',
                            isSelected ? 'bg-base-600' : 'hover:bg-base-700/60',
                          ].join(' ')}
                        >
                          <span className="block text-xs text-slate-400 tabular-nums">
                            {entry?.bid ?? '–'}/{entry?.tricksWon ?? '–'}
                          </span>
                          <span
                            className={[
                              'block font-semibold tabular-nums',
                              entry?.points == null
                                ? 'text-slate-600'
                                : entry.points >= 0
                                  ? 'text-good'
                                  : 'text-bad',
                            ].join(' ')}
                          >
                            {entry?.points == null ? '·' : signed(entry.points)}
                          </span>
                          <span className="block text-xs text-slate-500 tabular-nums">
                            {round.status === 'done' ? totals[roundIndex][seat.seat] : ''}
                          </span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-white/10">
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-base-800 px-2 py-3 text-left text-xs font-medium text-slate-400"
                >
                  Totaal
                </th>
                {game.seats.map((seat) => (
                  <td
                    key={seat.seat}
                    className="px-3 py-3 text-right text-lg font-bold tabular-nums"
                  >
                    {finalTotals[seat.seat]}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Per vakje: bod/gehaald, de punten van die ronde, en de stand erna. Tik een vakje aan voor
          de berekening.
        </p>
      </Panel>

      {selectedRound && selectedEntry && (
        <Panel title={`Ronde ${selectedRound.roundNo} · ${game.seats[selected!.seat].displayName}`}>
          {selectedEntry.bid === null || selectedEntry.tricksWon === null ? (
            <p className="text-sm text-slate-400">Deze ronde is nog niet ingevuld.</p>
          ) : (
            <div className="space-y-2 text-sm">
              <p>
                Bod {selectedEntry.bid}, gehaald {selectedEntry.tricksWon} van {selectedRound.cards}{' '}
                {plural(selectedRound.cards, 'slag', 'slagen')}.
              </p>
              <p className="text-slate-300">
                {selectedEntry.bid === selectedEntry.tricksWon ? (
                  <>
                    Precies goed: {scoring.exactBase}
                    {scoring.perTrickBid > 0 && (
                      <>
                        {' '}
                        + {scoring.perTrickBid === 1 ? '' : `${scoring.perTrickBid} × `}
                        {selectedEntry.bid}
                      </>
                    )}{' '}
                    = <strong className="text-good">{signed(selectedEntry.points ?? 0)}</strong>
                  </>
                ) : (
                  <>
                    {Math.abs(selectedEntry.tricksWon - selectedEntry.bid)}{' '}
                    {plural(
                      Math.abs(selectedEntry.tricksWon - selectedEntry.bid),
                      'slag',
                      'slagen'
                    )}{' '}
                    ernaast × {scoring.missPenaltyPerTrick} strafpunten
                    {scoring.alsoScoreTricksOnMiss &&
                      ` + ${selectedEntry.tricksWon} voor de slagen`}{' '}
                    = <strong className="text-bad">{signed(selectedEntry.points ?? 0)}</strong>
                  </>
                )}
              </p>
              {selectedRound.trump !== undefined && (
                <p className="text-slate-500">Troef was {trumpLabel(selectedRound.trump)}.</p>
              )}
            </div>
          )}

          {selectedRound.status === 'done' && (
            <div className="mt-3">
              <Button
                variant="danger"
                full
                onClick={() => {
                  reopenRound(game.id, selectedRound.roundNo);
                  setSelected(null);
                }}
              >
                Ronde {selectedRound.roundNo} opnieuw invullen
              </Button>
              <p className="mt-2 text-xs text-slate-500">
                De rest van het potje blijft staan; alleen deze ronde wordt opnieuw geteld.
              </p>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
